// Core Modules
import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';

// External Modules
import { Subscription } from 'rxjs';

// Services
import { AccessControlService } from '@core/services/access-control/access-control.service';
import { AuthService } from '@core/services/auth/auth.service';
import { SessionManagerService } from '@core/services/session-manager/session-manager.service';

// Models
import { IAuth0ClientInfo, IClientList } from '@shared/models/auth0-types.model';

@Component({
  selector: 'app-client-list',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './client-list.component.html',
  styleUrls: ['./client-list.component.scss', '../auth-login/auth-login.component.scss'],
})
export class ClientListComponent implements OnInit, OnDestroy {
  // Injectors
  private readonly accessControlService = inject(AccessControlService);
  private readonly router = inject(Router);
  private readonly sessionManager = inject(SessionManagerService);
  public readonly authService = inject(AuthService);

  // Private Members
  private readonly subscription = new Subscription();

  /**
   * Method to get clients
   */
  get clients(): IAuth0ClientInfo[] {
    return this.authService.clientList;
  }

  /**
   * Method to set current client
   */
  set clients(clientList: IAuth0ClientInfo[]) {
    this.authService.clientList = clientList;
  }

  public ngOnInit(): void {
    this.authService.authenticationInProgressSubject.next(true);
    this.subscription.add(
      this.authService.getClients().subscribe({
        next: (clients: IClientList) => {
          this.authService.authenticationInProgressSubject.next(false);
          this.clients = clients.items;
        },
        error: (error: HttpErrorResponse) => {
          console.log(`<Client-list> - <ngOnInit> Error occurred during get clients...`, error);
          this.authService.authenticationInProgressSubject.next(false);
          this.sessionManager.stopWatching();
          this.authService.logout();
        },
      })
    );
  }

  /**
   * Selecting Client from ClientList
   * @param {IAuth0ClientInfo} client
   * @memberof AuthLoginComponent
   */
  public selectClient(client: IAuth0ClientInfo): void {
    this.authService.updateClientInfo(client);
    this.authService.authenticationInProgressSubject.next(true);
    // Load the permissions and get the landing page as per the user permission
    this.subscription.add(
      this.accessControlService.definePermissions().subscribe({
        next: () => {
          this.authService.sendDataToGainSights();
          this.accessControlService.getLandingPageBasedOnUserScopes().then((landingPage: string) => this.router.navigate([landingPage]).then(() => this.authService.authenticationInProgressSubject.next(false)));
        },
        error: (e) => {
          this.authService.authenticationInProgressSubject.next(false);
          console.log(`<Client-list> - <selectClient> Error occurred during subscribing permissions...`, e);
          this.sessionManager.stopWatching();
          this.authService.logout();
        },
      })
    );
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
